import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { STORE_ACCESSORIES_URL, STORE_PARTS_URL } from '../../data/storeCatalog';

export function AccessoriesPartsPreview() {
  return (
    <section className="py-28 bg-[var(--zw-sand)] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-12 right-[10%] h-64 w-64 rounded-full bg-white/30 blur-3xl" />
        <div className="absolute bottom-12 left-[8%] h-56 w-56 rounded-full bg-[#dce6d8]/50 blur-3xl" />
      </div>

      <div className="home-shell">
        <div className="max-w-3xl mb-14">
          <motion.span
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="home-kicker"
          >
            Accesorios y recambios
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="home-title mb-5"
          >
            Todo lo que necesitas para equipar, cuidar y poner a punto tu Zwheel
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="home-copy"
          >
            Encuentra accesorios para tu dia a dia y recambios compatibles con tu modelo para mantener tu Zwheel siempre listo.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr_0.95fr] gap-6">
          <motion.article
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="group overflow-hidden rounded-[2rem] bg-white border border-black/5 shadow-[0_20px_70px_rgba(0,0,0,0.06)]"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src="/images/access/accesorios.jpg"
                alt="Accesorios Zwheel"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="text-xs uppercase tracking-[0.18em] text-white/65 mb-2">Accesorios</div>
                <h3 className="text-3xl font-bold text-white mb-3">Equipa tu Zwheel para el dia a dia</h3>
              </div>
            </div>
            <div className="p-6 md:p-7">
              <p className="text-gray-600 leading-relaxed mb-6">
                Cascos, candados, bolsas, soportes y mas accesorios para moverte con mas comodidad y seguridad.
              </p>
              <Button asChild variant="outline">
                <a href={STORE_ACCESSORIES_URL} target="_blank" rel="noopener noreferrer">
                  Explorar accesorios
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="group overflow-hidden rounded-[2rem] border border-white/8 bg-[#121212] text-white shadow-[0_24px_80px_rgba(0,0,0,0.18)]"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src="/images/access/recambios.jpg"
                alt="Recambios Zwheel"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="text-xs uppercase tracking-[0.18em] text-white/65 mb-2">Recambios</div>
                <h3 className="text-3xl font-bold text-white mb-3">Encuentra el recambio adecuado para tu modelo</h3>
              </div>
            </div>
            <div className="border-t border-white/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.035)_0%,rgba(0,0,0,0)_100%)] p-6 md:p-7">
              <p className="text-white/72 leading-relaxed mb-6">
                Localiza ruedas, frenos, baterias y otras piezas compatibles para comprar con tranquilidad.
              </p>
              <Button asChild className="bg-white text-black hover:bg-[#f3eadc]">
                <a href={STORE_PARTS_URL} target="_blank" rel="noopener noreferrer">
                  Abrir recambios
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="rounded-[2rem] bg-[linear-gradient(160deg,#18392b_0%,#214a37_100%)] text-white p-7 shadow-[0_24px_80px_rgba(24,57,43,0.2)] flex flex-col"
          >
            <div className="text-sm uppercase tracking-[0.18em] text-white/60 mb-4">Guia oficial</div>
            <h3 className="text-3xl font-bold mb-4">Identifica tu modelo y acierta a la primera</h3>
            <p className="text-white/74 leading-relaxed mb-6">
              Si no estas seguro de tu modelo, te guiamos para encontrar accesorios y recambios compatibles sin perder tiempo.
            </p>
            <div className="rounded-[1.4rem] border border-white/12 bg-white/8 p-5 mb-6">
              <div className="text-xs uppercase tracking-[0.18em] text-white/55 mb-4">Como empezar</div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#f3d59b] mt-0.5 shrink-0" />
                  <p className="text-sm leading-relaxed text-white/80">Identifica el modelo exacto de tu Zwheel.</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#f3d59b] mt-0.5 shrink-0" />
                  <p className="text-sm leading-relaxed text-white/80">Encuentra accesorios y recambios compatibles.</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#f3d59b] mt-0.5 shrink-0" />
                  <p className="text-sm leading-relaxed text-white/80">Accede a la ayuda oficial antes de comprar si la necesitas.</p>
                </div>
              </div>
            </div>
            <Button asChild className="w-fit bg-white text-black hover:bg-[#f3eadc]">
              <Link to="/accesorios-recambios">
                Ir a la guia
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
